import "./index.scss";
import { Button } from "@wise-emotions/react-button";
import { CfNewsEventsCategory } from "types/types";
import {
  CfNewsEventsDetailPreview,
  NewsEventsProps,
  SlideNewsEventProps,
} from "./types";
import { getTranslations } from "helpers/contenful";
import { useGetNewsEventsCategories } from "helpers/staticQueries/useGetNewsEventsCategories";
import { useGetNewsEventsDetail } from "helpers/staticQueries/useGetNewsEventsDetail";
import Breadcrumb from "components/Breadcrumb";
import CfCard07 from "components/Cards/Card07/CfCard07";
import CtaReference from "components/CtaReference";
import Filter from "components/Filter";
import Loader from "components/Loader";
import Markdown from "react-markdown";
import React, { useMemo, useRef, useState } from "react";
import Slider from "components/Slider";

const LIMIT_CHARS_TITLE = 60;
const LIMIT_CHARS_PARAGRAPH = 80;

const SlideNewsEvent = ({ data }: SlideNewsEventProps) => {
  const {
    abstract,
    contentType,
    currentItemPageUrl,
    date,
    image,
    labelCtaPreview,
    title,
  } = data;

  return (
    <CtaReference
      ctaType="internalStaticAutogenLink"
      internalStaticLink={currentItemPageUrl}
    >
      <div className="SlideNewsEvent">
        <div className="SlideNewsEvent__col">
          <div className="SlideNewsEvent__image-wrap">
            <img
              className="SlideNewsEvent__image"
              src={image[0].imageDesktop.file.url}
              alt={image[0].alt ?? ""}
            />
          </div>
        </div>
        <div className="SlideNewsEvent__col SlideNewsEvent__content">
          <div className="SlideNewsEvent__info">
            <div className="SlideNewsEvent__head">
              <span className="SlideNewsEvent__head__type">{contentType}</span>
              <span className="SlideNewsEvent__head__date">{date}</span>
            </div>
            {title && (
              <div className="SlideNewsEvent__title">
                <h3>{`${title.slice(0, LIMIT_CHARS_TITLE)}...`}</h3>
              </div>
            )}
            {abstract?.abstract && (
              <div className="SlideNewsEvent__abstract">
                {`${abstract.abstract.slice(0, LIMIT_CHARS_PARAGRAPH)}...`}
              </div>
            )}
            {labelCtaPreview?.[0].text?.text && (
              <div className="SlideNewsEvent__cta-label link-cta link-cta-label link-cta-label--accent">
                {labelCtaPreview[0].text.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </CtaReference>
  );
};

const ELEMENTS_IN_SLIDER = 4;
const LIMIT_IN_LIST = 6;
const TIMEOUT_LOADER = 400;
const AUTOPLAY_DELAY = 6000;

const NewsEvents = ({ page, breadcrumb }: NewsEventsProps) => {
  // TODO: !!!! fix when exist news in en
  // const node_locale = page?.node_locale;
  const node_locale = "it";
  const { title, subtitle, text } = page?.content?.[0] ?? {};

  const [numElementLimitInList, setNumElementLimitInList] =
    useState(LIMIT_IN_LIST);
  const [loader, setLoader] = useState(false);

  const newsEventsList = useRef<CfNewsEventsDetailPreview[]>([]);

  const newsEventsCategories: CfNewsEventsCategory[] =
    useGetNewsEventsCategories(node_locale);

  const [filterCategories, setFilterCategories] = useState<string[]>([]);

  const objTranslation = useMemo(() => {
    const translations = page?.settings[0]?.translations ?? [];
    return getTranslations(translations);
  }, [page]);

  const updateFilters = (slug: string) => {
    setLoader(true);
    let filterUpdated = [...filterCategories];
    if (!filterCategories.includes(slug)) {
      filterUpdated.push(slug);
    } else {
      filterUpdated = filterCategories.filter((item) => item !== slug);
    }
    setTimeout(() => {
      setFilterCategories(filterUpdated);
      setNumElementLimitInList(LIMIT_IN_LIST);
      setLoader(false);
    }, TIMEOUT_LOADER);
  };

  const { list, total } = useGetNewsEventsDetail(
    node_locale,
    filterCategories,
    numElementLimitInList,
  );

  newsEventsList.current = list;

  const newsEventsListForSlider = React.useMemo(() => {
    return list.slice(0, ELEMENTS_IN_SLIDER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => {
    setLoader(true);
    setTimeout(() => {
      setNumElementLimitInList(numElementLimitInList + LIMIT_IN_LIST);
      setLoader(false);
    }, TIMEOUT_LOADER);
  };

  return (
    <div className="NewsEvents">
      <div className="NewsEvents__head">
        <div className="wrapContent wrapContent-lg-lg">
          <div className="NewsEvents__title">
            <h1>{title}</h1>
          </div>
          {subtitle?.subtitle && (
            <div className="NewsEvents__subtitle">{subtitle.subtitle}</div>
          )}
        </div>
      </div>
      <div className="NewsEvents__slider">
        <div className="NewsEvents__slider__curved curved--bottom" />
        <div className="wrapContent wrapContent-lg-lg">
          <div className="NewsEvents__slider__wrap">
            <Slider id="newsEventSlider" autoplay={{ delay: AUTOPLAY_DELAY }}>
              {newsEventsListForSlider.map((item) => {
                return (
                  <div className="NewsEvents__slider__slide" key={item.id}>
                    <SlideNewsEvent data={item} />
                  </div>
                );
              })}
            </Slider>
          </div>
        </div>
      </div>
      {breadcrumb && (
        <div className="NewsEvents__breadcrumbs">
          <Breadcrumb {...breadcrumb} />
        </div>
      )}
      <div className="NewsEvents__content">
        <div className="wrapContent wrapContent-lg-lg">
          {loader && (
            <div className="NewsEvents__content__loader">
              <Loader loading={loader} full />
            </div>
          )}
          {text?.text && (
            <div className="NewsEvents__text editorial">
              <Markdown>{text.text}</Markdown>
            </div>
          )}
          <div className="NewsEvents__filters">
            <div className="NewsEvents__filters__wrap">
              {newsEventsCategories.map((filter) => {
                return (
                  <div className="NewsEvents__filters__item" key={filter.id}>
                    <Filter
                      // TODO: when nullable from title will be removed, remove also ?? ""
                      title={filter.title ?? ""}
                      onClick={() => updateFilters(filter.slug)}
                      isPressed={filterCategories.includes(filter.slug)}
                      key={filter.id}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="NewsEvents__list row">
            {newsEventsList.current.map((item) => {
              return (
                <div
                  className="NewsEvents__item col col-12 col-sm-6 col-md-4"
                  key={item.id}
                >
                  <CtaReference
                    ctaType="internalStaticAutogenLink"
                    internalStaticLink={item.currentItemPageUrl}
                  >
                    <CfCard07
                      abstract={item.abstract}
                      headingLabelType={item.contentType}
                      headingLabelInfo={item.date}
                      image={item.image}
                      // @ts-expect-error
                      labelCta={item.labelCtaPreview}
                      title={item.title}
                      contentful_id={item.contentful_id}
                    />
                  </CtaReference>
                </div>
              );
            })}
          </div>
          {total > newsEventsList.current.length && (
            <div className="NewsEvents__load-more">
              <Button variant="default" onClick={() => loadMore()}>
                {objTranslation?.["load-more"] ?? ""}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsEvents;
